'use client';

import React, { useState, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Box, Button, Paper, Typography, CircularProgress } from '@mui/material';
import useFetchProjectTracker from '../../hooks/useFetchProjectTracker';
import { ModuleRegistry } from 'ag-grid-community';
import {
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PivotModule,
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PivotModule,
]);

import ProgressBarCellRenderer from '../ProjectTracker/ProgressBarCellRenderer';

const ProjectSummaryGrid = ({ onLoaded }) => {
  const { projects, loading, error } = useFetchProjectTracker();
  const [rowData, setRowData] = useState([]);
  const hasCalledOnLoaded = useRef(false);

  useEffect(() => {
    if (projects.length > 0) {
      setRowData(projects);
    }
  }, [projects]);
  useEffect(() => {
    if (!loading && projects.length > 0 && !hasCalledOnLoaded.current) {
      onLoaded?.();
      hasCalledOnLoaded.current = true;
    }
  }, [loading, projects, onLoaded]);

  const columnDefs = [
    {
      headerName: 'Entity',
      field: 'entity',
      enableRowGroup: true,
      rowGroup: true,
    },
    {
      headerName: 'Project Name',
      field: 'name',
    },
    {
      headerName: 'AFE No.',
      field: 'afeNumber',
    },
    {
      headerName: 'Actual Cost',
      field: 'actuals',
      enableValue: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value);
        } else {
          return '';
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.actuals ?? 0;
          });
        }

        return sumTotal;
      },
    },
    {
      headerName: 'Committed',
      field: 'committed',
      enableValue: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value);
        } else {
          return '';
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.committed ?? 0;
          });
        }

        return sumTotal;
      },
    },
    {
      headerName: 'Fixed Cost',
      field: 'fixedCost',
      enableValue: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value);
        } else {
          return '';
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.fixedCost ?? 0;
          });
        }

        return sumTotal;
      },
    },
    {
      headerName: 'Base Cost',
      field: 'baseCost',
      enableValue: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value);
        } else {
          return '';
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.baseCost ?? 0;
          });
        }

        return sumTotal;
      },
    },
    {
      headerName: 'EFC',
      field: 'efc',
      enableValue: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value);
        } else {
          return '';
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.efc ?? 0;
          });
        }

        return sumTotal;
      },
    },
    {
      headerName: 'Budget Cost',
      field: 'budgetCost',
      enableValue: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value);
        } else {
          return '';
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.budgetCost ?? 0;
          });
        }

        return sumTotal;
      },
    },

    {
      headerName: 'Efc to Budget',
      field: 'efcBudget',
      cellRenderer: ProgressBarCellRenderer,
      valueGetter: function (params) {
        if (!params.data || params.data.efc === undefined || params.data.budgetCost === undefined) {
          return null;
        }

        const efc = params.data.efc ?? 0;
        const budget = params.data.budgetCost ?? 0;

        if (budget !== 0) {
          return (efc / budget) * 100;
        } else {
          return null;
        }
      },
      aggFunc: function (nodes) {
        let sumTotalBilled = 0;
        let sumNetAmount = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotalBilled += node.data?.efc ?? 0;
            sumNetAmount += node.data?.budgetCost ?? 0;
          });
        }

        return sumNetAmount !== 0 ? (sumTotalBilled / sumNetAmount) * 100 : 0;
      },
    },
    {
      headerName: 'Remaining Contingency',
      field: 'remainingContingency',
      enableValue: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value);
        } else {
          return '';
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.remainingContingency ?? 0;
          });
        }

        return sumTotal;
      },
    },
    {
      headerName: 'Over / Under Budget',
      field: 'overUnderBudget',
      enableValue: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value);
        } else {
          return '';
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.overUnderBudget ?? 0;
          });
        }

        return sumTotal;
      },
      cellStyle: (params) => {
        if (params.value < 0) {
          return { backgroundColor: '#00BBA1', color: 'white' };
        } else if ((params.value = 0 || params.value == null)) {
          return null;
        } else {
          return { backgroundColor: '#FF594B', color: 'white' };
        }
      },
    },
    {
      headerName: 'Contingency %',
      field: 'contingencyPct',
      enableValue: true,
      cellRenderer: 'progressBarCellRenderer',
      valueGetter: function (params) {
        const contingencyPct = params.data.contingencyPct;
        if (contingencyPct != null) {
          return contingencyPct * 100;
        } else {
          return null;
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.contingencyPct ?? 0;
          });
        }

        return sumTotal;
      },
    },
    {
      headerName: 'Total Cost Per Foot',
      field: 'totalCostPerFoot',
      enableValue: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value);
        } else {
          return '';
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.totalCostPerFoot ?? 0;
          });
        }

        return sumTotal;
      },
    },
    {
      headerName: '$ / In-Mile',
      field: 'costPerInMile',
      enableValue: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value);
        } else {
          return '';
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.costPerInMile ?? 0;
          });
        }

        return sumTotal;
      },
    },
    {
      headerName: '$ / ROD',
      field: 'costPerRod',
      enableValue: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value);
        } else {
          return '';
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.costPerRod ?? 0;
          });
        }

        return sumTotal;
      },
    },
    {
      headerName: '$ / Foot ROW',
      field: 'costPerFootRow',
      enableValue: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value);
        } else {
          return '';
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.costPerFootRow ?? 0;
          });
        }

        return sumTotal;
      },
    },
    {
      headerName: 'Cost Per BHP',
      field: 'costPerBhp',
      enableValue: true,
      valueFormatter: (params) => {
        if (params.value) {
          return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value);
        } else {
          return '';
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.costPerBhp ?? 0;
          });
        }

        return sumTotal;
      },
    },
    {
      headerName: 'Variance %',
      field: 'variancePct',
      enableValue: true,
      cellRenderer: ProgressBarCellRenderer,
      valueGetter: function (params) {
        const variancePct = params.data.variancePct;
        if (variancePct != null) {
          return variancePct * 100;
        } else {
          return null;
        }
      },
      aggFunc: function (nodes) {
        let sumTotal = 0;

        if (Array.isArray(nodes.rowNode.allLeafChildren)) {
          nodes.rowNode.allLeafChildren.forEach(function (node) {
            sumTotal += node.data?.variancePct ?? 0;
          });
        }

        return sumTotal;
      },
    },
    {
      headerName: 'Project Phase',
      field: 'projectPhase',
    },
    {
      headerName: 'Type',
      field: 'type',
    },
    {
      headerName: 'Project ID',
      field: 'projectId',
      hide: true,
    },
    {
      headerName: 'Number of Notes',
      field: 'numberOfNotes',
    },
    {
      headerName: 'Project Manager',
      field: 'projectManager',
      enableRowGroup: true,
    },
    {
      headerName: 'Last Modified By',
      field: 'lastModifiedBy',
      sortable: true,
      filter: true,
      enableRowGroup: true,
      pivot: true,
    },
    {
      headerName: 'Last Modified Date/Time',
      field: 'lastModifiedDate',
    },
  ];

  return (
    <Box>
      {loading ? (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6">Loading Data...</Typography>
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', color: 'red' }}>
          <Typography variant="h6">Error: {error}</Typography>
        </Box>
      ) : (
        <AgGridReact
          columnDefs={columnDefs}
          rowData={rowData}
          //pagination={true}
          cellSelection={true}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
          }}
          rowGroupPanelShow="always"
          animateRows={true}
          domLayout="autoHeight"
          components={{
            progressBarCellRenderer: ProgressBarCellRenderer,
          }}
        />
      )}
    </Box>
  );
};

export default ProjectSummaryGrid;
